// Bulk Test Records panel — Admin-only.
//
// Lives at the top of the Score Report tab. Flow:
//   1. Admin picks an intake date and uploads an .xlsx with
//      (first_name, last_name, program_name) rows.
//   2. Backend fuzzy-matches each row against the active students collection
//      and buckets them into matched / review / not_found.
//   3. For each matched row the admin clicks "Make Test Record". That creates
//      a Test Record (separate from regular score reports), pulling the
//      student's name + DOB from their parsed ID (passport / PR / DL).
//   4. Generated Test Records show up in the "Test Records history" strip
//      below with Preview / Download / Delete actions.
//
// These records never surface on the student profile.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { API } from "@/config/api";
import {
  FileArrowUp, CircleNotch, CheckCircle, WarningCircle,
  XCircle, FileText, DownloadSimple, Trash, ArrowClockwise, Stack,
} from "@phosphor-icons/react";

export default function BulkTestRecordsPanel() {
  const [intakeDate, setIntakeDate] = useState("");
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null); // student_id being generated

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/admin/test-score-reports`);
      setHistory(data.records || []);
    } catch (err) {
      toast.error("Could not load test records history");
    } finally {
      setLoadingHistory(false);
    }
  };
  useEffect(() => { loadHistory(); }, []);

  const handlePreview = async () => {
    if (!file) { toast.error("Choose an .xlsx file first"); return; }
    if (!intakeDate) { toast.error("Pick an intake date"); return; }
    setPreviewing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("intake_date", intakeDate);
      const { data } = await axios.post(`${API}/admin/test-score-reports/preview-excel`, fd);
      setPreview(data);
      toast.success(`Parsed ${data.total_rows} row${data.total_rows === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse that sheet");
    } finally {
      setPreviewing(false);
    }
  };

  const handleCreate = async (studentId) => {
    if (!preview?.intake_date) return;
    setCreatingFor(studentId);
    try {
      const { data } = await axios.post(`${API}/admin/test-score-reports/create`, {
        student_id: studentId, intake_date: preview.intake_date,
      });
      toast.success(`Test record created · ${data.name_used}`);
      setHistory((prev) => [data, ...prev]);
      _markRow(studentId, { _created: true, _error: null });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create test record";
      // Mark the row with the error so inline upload can appear.
      _markRow(studentId, { _error: msg });
      toast.error(msg);
    } finally {
      setCreatingFor(null);
    }
  };

  const _markRow = (studentId, patch) => {
    setPreview((p) => p ? {
      ...p,
      matched: p.matched.map((m) => m.student_id === studentId ? { ...m, ...patch } : m),
      matched_by_intake: (p.matched_by_intake || []).map((g) => ({
        ...g, rows: g.rows.map((r) => r.student_id === studentId ? { ...r, ...patch } : r),
      })),
    } : p);
  };

  const [uploadingFor, setUploadingFor] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, failed: 0 });

  // History bulk-download selection + state
  const [selectedHistoryIds, setSelectedHistoryIds] = useState(() => new Set());
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [historyFilter, setHistoryFilter] = useState("all"); // all | duplicates | olderDups

  // Compute duplicate groups (same student_id → N records). The newest record
  // in each group is the "keeper"; the rest are "older duplicates" that the
  // admin probably wants to delete.
  const dupMeta = React.useMemo(() => {
    const groups = {};
    history.forEach((rec) => {
      const k = rec.student_id || "_unknown";
      (groups[k] = groups[k] || []).push(rec);
    });
    const dupIds = new Set();
    const olderDupIds = new Set();
    Object.values(groups).forEach((rows) => {
      if (rows.length < 2) return;
      rows.forEach((r) => dupIds.add(r.id));
      // Sort newest-first and mark everything except the first as "older dup"
      const sorted = [...rows].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      sorted.slice(1).forEach((r) => olderDupIds.add(r.id));
    });
    return { groups, dupIds, olderDupIds };
  }, [history]);

  const visibleHistory = React.useMemo(() => {
    let rows = history;
    if (historyFilter === "duplicates") rows = rows.filter((r) => dupMeta.dupIds.has(r.id));
    else if (historyFilter === "olderDups") rows = rows.filter((r) => dupMeta.olderDupIds.has(r.id));
    const q = historySearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) => (r.name_used || "").toLowerCase().includes(q) || (r.student_full_name_on_profile || "").toLowerCase().includes(q) || (r.exnum || "").includes(q));
    return rows;
  }, [history, historyFilter, historySearch, dupMeta]);

  const toggleHistoryOne = (id) => {
    setSelectedHistoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleHistoryAll = (checked) => {
    if (!checked) { setSelectedHistoryIds(new Set()); return; }
    setSelectedHistoryIds(new Set(visibleHistory.map((h) => h.id)));
  };
  const selectAllOlderDups = () => setSelectedHistoryIds(new Set(dupMeta.olderDupIds));

  const handleBulkDownloadZip = async () => {
    const ids = Array.from(selectedHistoryIds);
    if (!ids.length) { toast.error("Select at least one record"); return; }
    setDownloadingZip(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
      const resp = await fetch(`${API}/admin/test-score-reports/bulk-download`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Test_Records_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Downloaded ${ids.length} record${ids.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Could not build the zip");
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedHistoryIds);
    if (!ids.length) { toast.error("Select at least one record"); return; }
    if (!window.confirm(`Permanently delete ${ids.length} test record${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-score-reports/bulk-delete`, { ids });
      setHistory((prev) => prev.filter((h) => !ids.includes(h.id)));
      setSelectedHistoryIds(new Set());
      toast.success(`Deleted ${data.deleted} record${data.deleted === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete records");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Review-bucket: search a different student when the nearest is wrong
  const [reviewPickerFor, setReviewPickerFor] = useState(null); // row key
  const [reviewSearchQuery, setReviewSearchQuery] = useState("");
  const [reviewSearchResults, setReviewSearchResults] = useState([]);
  const [reviewSearchLoading, setReviewSearchLoading] = useState(false);
  useEffect(() => {
    if (!reviewPickerFor) return;
    if (reviewSearchQuery.trim().length < 2) { setReviewSearchResults([]); return; }
    const t = setTimeout(async () => {
      setReviewSearchLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/test-score-reports/lookup-student`, { params: { q: reviewSearchQuery.trim() } });
        setReviewSearchResults(data.results || []);
      } catch { setReviewSearchResults([]); }
      finally { setReviewSearchLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [reviewSearchQuery, reviewPickerFor]);

  const [reviewCreatingFor, setReviewCreatingFor] = useState(null);
  const createForReviewRow = async (rowKey, studentId) => {
    if (!preview?.intake_date || !studentId) return;
    setReviewCreatingFor(rowKey);
    try {
      const { data } = await axios.post(`${API}/admin/test-score-reports/create`, {
        student_id: studentId, intake_date: preview.intake_date,
      });
      toast.success(`Test record created · ${data.name_used}`);
      setHistory((prev) => [data, ...prev]);
      // Mark the review row as done inline.
      setPreview((p) => p ? {
        ...p,
        review: p.review.map((r, idx) => (`r${idx}` === rowKey ? { ...r, _created: true, _created_name: data.name_used } : r)),
      } : p);
      setReviewPickerFor(null);
      setReviewSearchQuery("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create test record");
    } finally {
      setReviewCreatingFor(null);
    }
  };

  const _allMatchedActionableIds = () =>
    (preview?.matched || []).filter((m) => !m._created).map((m) => m.student_id);
  const toggleOne = (sid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };
  const toggleGroup = (rows, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      rows.filter((r) => !r._created).forEach((r) => {
        if (checked) next.add(r.student_id); else next.delete(r.student_id);
      });
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (!checked) { setSelectedIds(new Set()); return; }
    setSelectedIds(new Set(_allMatchedActionableIds()));
  };

  const handleBulkCreate = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error("Select at least one student"); return; }
    if (!preview?.intake_date) return;
    const total = ids.length;
    if (!window.confirm(`Create test records for ${total} student${total === 1 ? "" : "s"}? This may take a few minutes if some IDs need OCR.`)) return;
    setBulkRunning(true);
    setBulkProgress({ done: 0, total, failed: 0 });
    try {
      // Chunk client-side so the admin sees progress ticks even with many
      // students. Backend processes 4 per chunk concurrently.
      const CHUNK = 12;
      let done = 0, failed = 0;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        try {
          const { data } = await axios.post(`${API}/admin/test-score-reports/bulk-create`, {
            student_ids: slice, intake_date: preview.intake_date,
          });
          // Mark successes
          (data.created || []).forEach((rec) => {
            _markRow(rec.student_id, { _created: true, _error: null });
            setHistory((prev) => [rec, ...prev]);
          });
          (data.failed || []).forEach((f) => {
            _markRow(f.student_id, { _error: f.error });
          });
          done += (data.created || []).length;
          failed += (data.failed || []).length;
        } catch (err) {
          // Whole chunk errored — mark each as failed.
          slice.forEach((sid) => _markRow(sid, { _error: err?.response?.data?.detail || "Server error" }));
          failed += slice.length;
        }
        setBulkProgress({ done, total, failed });
      }
      setSelectedIds(new Set());
      if (failed === 0) {
        toast.success(`Done! ${done} test record${done === 1 ? "" : "s"} created.`);
      } else {
        toast.warning(`Finished: ${done} created, ${failed} failed. Review the rows marked in red.`);
      }
    } finally {
      setBulkRunning(false);
    }
  };
  const handleInlineUpload = async (studentId, fileObj) => {
    if (!fileObj) return;
    if (!preview?.intake_date) { toast.error("Missing intake date"); return; }
    setUploadingFor(studentId);
    try {
      const fd = new FormData();
      fd.append("student_id", studentId);
      fd.append("intake_date", preview.intake_date);
      fd.append("file", fileObj);
      const { data } = await axios.post(`${API}/admin/test-score-reports/upload-id-and-create`, fd);
      toast.success(`Test record created · ${data.name_used}`);
      setHistory((prev) => [data, ...prev]);
      _markRow(studentId, { _created: true, _error: null });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create test record from upload";
      _markRow(studentId, { _error: msg });
      toast.error(msg);
    } finally {
      setUploadingFor(null);
    }
  };

  const handleDownloadRecord = async (rec) => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
      const resp = await fetch(`${API}/admin/test-score-reports/${rec.id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = rec.filename || "test-record.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast.error("Could not download that record");
    }
  };

  const handlePreviewRecord = async (rec) => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token") || "";
      const resp = await fetch(`${API}/admin/test-score-reports/${rec.id}/file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error("Could not open that record");
    }
  };

  const handleDeleteRecord = async (rec) => {
    if (!window.confirm(`Delete the test record for ${rec.name_used}? This cannot be undone.`)) return;
    setDeletingId(rec.id);
    try {
      await axios.delete(`${API}/admin/test-score-reports/${rec.id}`);
      setHistory((prev) => prev.filter((h) => h.id !== rec.id));
      toast.success("Test record deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete that record");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {/* Upload + preview card */}
      <div className="bg-white border border-indigo-200 rounded-lg p-5" data-testid="bulk-test-records-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">Bulk Test Records from Excel</p>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Upload a sheet with <code>First Name</code>, <code>Last Name</code>, <code>Program Name</code>. We match against your students
              and let you generate test-record PDFs. Name + DOB on the PDF come from each student&rsquo;s uploaded ID.
            </p>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-gray-400">Step 00 · Admin only</span>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs text-gray-700">Intake date</Label>
            <Input type="date" value={intakeDate} onChange={(e) => setIntakeDate(e.target.value)} data-testid="btr-intake-date" />
            <p className="text-[11px] text-gray-500">Test dates land 3-6 months before this.</p>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs text-gray-700">Excel file (.xlsx)</Label>
            <div className="flex items-center gap-2">
              <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="btr-file-input" />
              <Button onClick={handlePreview} disabled={previewing || !file || !intakeDate} data-testid="btr-preview-btn">
                {previewing ? (<><CircleNotch size={14} className="animate-spin mr-1.5" /> Parsing</>) : (<><FileArrowUp size={14} className="mr-1.5" /> Upload &amp; Match</>)}
              </Button>
            </div>
          </div>
        </div>

        {preview && (
          <div className="mt-5 space-y-4" data-testid="btr-preview-result">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Matched · {preview.matched.length}</span>
              <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Review · {preview.review.length}</span>
              <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Not found · {preview.not_found.length}</span>
              <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-700 border border-gray-200">Total rows · {preview.total_rows}</span>
            </div>
            {preview.warnings?.length > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}

            {/* Matched — grouped by intake */}
            {(preview.matched_by_intake || []).length > 0 && (
              <>
                {/* Sticky bulk-action bar */}
                <div className="sticky top-0 z-10 -mx-1 mb-2 bg-white border border-indigo-200 rounded-md shadow-sm px-3 py-2 flex items-center justify-between gap-3 flex-wrap" data-testid="btr-bulk-action-bar">
                  <div className="flex items-center gap-2 text-xs">
                    <label className="inline-flex items-center gap-1.5 font-medium text-gray-700 cursor-pointer">
                      <Checkbox
                        checked={selectedIds.size > 0 && selectedIds.size === _allMatchedActionableIds().length}
                        onCheckedChange={(v) => toggleAll(!!v)}
                        disabled={bulkRunning}
                        data-testid="btr-select-all"
                      />
                      Select all matched ({_allMatchedActionableIds().length})
                    </label>
                    {selectedIds.size > 0 && (
                      <span className="text-indigo-700 font-semibold">· {selectedIds.size} selected</span>
                    )}
                    {bulkRunning && (
                      <span className="text-gray-600">
                        · {bulkProgress.done}/{bulkProgress.total} done{bulkProgress.failed ? ` (${bulkProgress.failed} failed)` : ""}
                      </span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={handleBulkCreate}
                    disabled={bulkRunning || selectedIds.size === 0}
                    data-testid="btr-bulk-create-btn"
                  >
                    {bulkRunning ? (
                      <><CircleNotch size={13} className="animate-spin mr-1.5" /> Working… {bulkProgress.done}/{bulkProgress.total}</>
                    ) : (
                      <><Stack size={13} className="mr-1.5" /> Make test records for {selectedIds.size || "selected"}</>
                    )}
                  </Button>
                </div>

                <div className="space-y-4">
                {preview.matched_by_intake.map((group) => {
                  const actionable = group.rows.filter((r) => !r._created).map((r) => r.student_id);
                  const allSelected = actionable.length > 0 && actionable.every((id) => selectedIds.has(id));
                  return (
                  <div key={group.intake} className="border border-emerald-200 rounded-lg overflow-hidden">
                    <div className="bg-emerald-50 px-3 py-2 flex items-center justify-between gap-3">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-emerald-800">
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(v) => toggleGroup(group.rows, !!v)}
                          disabled={bulkRunning || actionable.length === 0}
                          data-testid={`btr-group-select-${group.intake}`}
                        />
                        <CheckCircle size={14} /> {group.intake} <span className="text-emerald-600">· {group.rows.length}</span>
                      </label>
                    </div>
                    <div className="divide-y divide-emerald-100">
                      {group.rows.map((r) => {
                        const disabled = r._created;
                        const isSelected = selectedIds.has(r.student_id);
                        return (
                          <div key={r.student_id} className="px-3 py-2 text-xs" data-testid={`btr-match-row-${r.student_id}`}>
                            <div className="flex items-center justify-between gap-3">
                              <label className="min-w-0 flex-1 flex items-start gap-2 cursor-pointer">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleOne(r.student_id)}
                                  disabled={disabled || bulkRunning}
                                  className="mt-0.5"
                                  data-testid={`btr-select-${r.student_id}`}
                                />
                                <span className="min-w-0 flex-1">
                                  <span className="font-medium text-gray-900 truncate block">{r.first_name} {r.last_name}
                                    <span className="text-gray-400 mx-1">→</span>
                                    <span className="text-emerald-800">{r.student_full_name}</span>
                                  </span>
                                  <span className="text-[11px] text-gray-500 truncate block">
                                    {r.program ? `Sheet: ${r.program} · ` : ""}{r.student_program ? `DB: ${r.student_program} · ` : ""}
                                    Score {r.match_score}
                                    {!r.has_parsed_id && <span className="ml-2 text-amber-700 font-medium">· Will OCR on first click</span>}
                                  </span>
                                </span>
                              </label>
                              <Button
                                size="sm"
                                variant={r._created ? "outline" : "default"}
                                disabled={disabled || creatingFor === r.student_id || uploadingFor === r.student_id || bulkRunning}
                                onClick={() => handleCreate(r.student_id)}
                                className="h-7 text-[11px]"
                                data-testid={`btr-make-record-${r.student_id}`}
                                title="Generate a test record for this student"
                              >
                                {creatingFor === r.student_id ? (
                                  <><CircleNotch size={12} className="animate-spin mr-1" /> Working</>
                                ) : r._created ? (
                                  <><CheckCircle size={12} className="mr-1" /> Done</>
                                ) : (
                                  <><FileText size={12} className="mr-1" /> Make test record</>
                                )}
                              </Button>
                            </div>
                            {r._error && !r._created && (
                              <div className="mt-2 ml-0 rounded border border-rose-200 bg-rose-50 px-2 py-1.5">
                                <div className="text-[11px] text-rose-700">{r._error}</div>
                                <div className="mt-1.5 flex items-center gap-2">
                                  <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-rose-900 cursor-pointer hover:text-rose-700">
                                    <FileArrowUp size={13} />
                                    {uploadingFor === r.student_id ? "Uploading & extracting…" : "Upload ID & retry"}
                                    <input
                                      type="file"
                                      accept="image/*,application/pdf"
                                      className="hidden"
                                      disabled={uploadingFor === r.student_id}
                                      onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        e.target.value = "";
                                        if (f) handleInlineUpload(r.student_id, f);
                                      }}
                                      data-testid={`btr-inline-upload-${r.student_id}`}
                                    />
                                  </label>
                                  {uploadingFor === r.student_id && <CircleNotch size={12} className="animate-spin text-rose-500" />}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
                </div>
              </>
            )}

            {/* Review bucket */}
            {preview.review.length > 0 && (
              <details className="border border-amber-200 rounded-lg overflow-hidden" open>
                <summary className="bg-amber-50 px-3 py-2 cursor-pointer text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                  <WarningCircle size={14} /> Needs review ({preview.review.length})
                </summary>
                <div className="divide-y divide-amber-100">
                  {preview.review.map((r, i) => {
                    const rowKey = `r${i}`;
                    const pickerOpen = reviewPickerFor === rowKey;
                    return (
                      <div key={rowKey} className="px-3 py-2 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900">Row {r.row}: {r.first_name} {r.last_name}</div>
                            <div className="text-[11px] text-gray-600">
                              Nearest match: <span className="text-amber-900 font-medium">{r.student_full_name || "—"}</span>
                              {r.student_program && <span className="text-gray-500"> · {r.student_program}</span>}
                              <span className="text-gray-400"> · score {r.match_score}</span>
                            </div>
                            {r._created && (
                              <div className="text-[11px] text-emerald-700 mt-0.5">✓ Test record created for <b>{r._created_name}</b></div>
                            )}
                          </div>
                          {!r._created && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              <Button
                                size="sm"
                                disabled={reviewCreatingFor === rowKey || !r.student_id}
                                onClick={() => createForReviewRow(rowKey, r.student_id)}
                                className="h-7 text-[11px]"
                                data-testid={`btr-review-confirm-${rowKey}`}
                                title="Create a test record using the nearest match"
                              >
                                {reviewCreatingFor === rowKey ? (
                                  <><CircleNotch size={12} className="animate-spin mr-1" /> Working</>
                                ) : (
                                  <><CheckCircle size={12} className="mr-1" /> Confirm &amp; make</>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px]"
                                onClick={() => { setReviewPickerFor(pickerOpen ? null : rowKey); setReviewSearchQuery(""); setReviewSearchResults([]); }}
                                data-testid={`btr-review-pick-other-${rowKey}`}
                              >
                                Pick someone else
                              </Button>
                            </div>
                          )}
                        </div>
                        {pickerOpen && !r._created && (
                          <div className="mt-2 rounded border border-amber-300 bg-white p-2">
                            <Input
                              autoFocus
                              placeholder="Search student by name (min 2 chars)…"
                              value={reviewSearchQuery}
                              onChange={(e) => setReviewSearchQuery(e.target.value)}
                              className="h-8 text-xs"
                              data-testid={`btr-review-search-input-${rowKey}`}
                            />
                            <div className="mt-2 max-h-56 overflow-auto divide-y divide-gray-100">
                              {reviewSearchLoading && <div className="text-[11px] text-gray-500 p-2">Searching…</div>}
                              {!reviewSearchLoading && reviewSearchResults.length === 0 && reviewSearchQuery.trim().length >= 2 && (
                                <div className="text-[11px] text-gray-500 p-2">No matches.</div>
                              )}
                              {reviewSearchResults.map((s) => (
                                <button
                                  key={s.student_id}
                                  type="button"
                                  className="w-full text-left px-2 py-1.5 hover:bg-amber-50 flex items-center justify-between gap-2"
                                  onClick={() => createForReviewRow(rowKey, s.student_id)}
                                  disabled={reviewCreatingFor === rowKey}
                                  data-testid={`btr-review-pick-${rowKey}-${s.student_id}`}
                                >
                                  <span className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-900 truncate">{s.full_name || "(unnamed)"}</div>
                                    <div className="text-[11px] text-gray-500 truncate">{s.program || "—"} · {s.intake || "—"}</div>
                                  </span>
                                  <span className="text-[11px] text-amber-700 shrink-0">Use this →</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            )}

            {/* Not-found bucket */}
            {preview.not_found.length > 0 && (
              <details className="border border-rose-200 rounded-lg overflow-hidden">
                <summary className="bg-rose-50 px-3 py-2 cursor-pointer text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                  <XCircle size={14} /> Not found ({preview.not_found.length})
                </summary>
                <div className="divide-y divide-rose-100">
                  {preview.not_found.map((r, i) => (
                    <div key={`${r.row}-${i}`} className="px-3 py-2 text-xs">
                      Row {r.row}: {r.first_name} {r.last_name} — {r.match_reason}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Test records history */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="btr-history-panel">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Test Records history</p>
            {dupMeta.dupIds.size > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700" data-testid="btr-dup-badge">
                {dupMeta.dupIds.size} duplicate · {dupMeta.olderDupIds.size} older copies
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedHistoryIds.size > 0 && (
              <>
                <span className="text-[11px] text-gray-600">{selectedHistoryIds.size} selected</span>
                <Button size="sm" onClick={handleBulkDownloadZip} disabled={downloadingZip || bulkDeleting} data-testid="btr-history-bulk-download">
                  {downloadingZip ? (<><CircleNotch size={13} className="animate-spin mr-1.5" /> Zipping…</>) : (<><DownloadSimple size={13} className="mr-1.5" /> Download (.zip)</>)}
                </Button>
                <Button size="sm" variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting || downloadingZip} data-testid="btr-history-bulk-delete">
                  {bulkDeleting ? (<><CircleNotch size={13} className="animate-spin mr-1.5" /> Deleting…</>) : (<><Trash size={13} className="mr-1.5" /> Delete selected</>)}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedHistoryIds(new Set())} data-testid="btr-history-clear-selection">Clear</Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={loadHistory} disabled={loadingHistory} data-testid="btr-refresh-history">
              {loadingHistory ? <CircleNotch size={13} className="animate-spin" /> : <ArrowClockwise size={13} />}
            </Button>
          </div>
        </div>
        {/* Filter / search / dup-helper row */}
        <div className="flex items-center gap-2 flex-wrap mb-3 text-xs">
          <Input
            placeholder="Search by name or exnum…"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            className="h-8 w-56 text-xs"
            data-testid="btr-history-search"
          />
          <select
            value={historyFilter}
            onChange={(e) => setHistoryFilter(e.target.value)}
            className="h-8 rounded border border-gray-200 bg-white px-2 text-xs"
            data-testid="btr-history-filter"
          >
            <option value="all">All records ({history.length})</option>
            <option value="duplicates">Only duplicates ({dupMeta.dupIds.size})</option>
            <option value="olderDups">Only older copies ({dupMeta.olderDupIds.size})</option>
          </select>
          {dupMeta.olderDupIds.size > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-[11px]"
              onClick={selectAllOlderDups}
              data-testid="btr-select-older-dups"
              title="Keeps the newest record per student; selects all older copies for deletion"
            >
              Select older copies ({dupMeta.olderDupIds.size})
            </Button>
          )}
        </div>
        {visibleHistory.length === 0 ? (
          <div className="text-xs text-gray-500 py-6 text-center">
            {history.length === 0
              ? "No test records yet. Upload a sheet above to get started."
              : "No records match the current filter."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-2">
                    <Checkbox
                      checked={visibleHistory.length > 0 && visibleHistory.every((h) => selectedHistoryIds.has(h.id))}
                      onCheckedChange={(v) => toggleHistoryAll(!!v)}
                      data-testid="btr-history-select-all"
                    />
                  </th>
                  <th className="py-2 pr-3">Student</th>
                  <th className="py-2 pr-3">DOB</th>
                  <th className="py-2 pr-3">Intake</th>
                  <th className="py-2 pr-3">Test date</th>
                  <th className="py-2 pr-3">Scores (R / W / WP)</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleHistory.map((rec) => {
                  const isOlderDup = dupMeta.olderDupIds.has(rec.id);
                  const isDup = dupMeta.dupIds.has(rec.id);
                  return (
                  <tr key={rec.id} data-testid={`btr-history-row-${rec.id}`} className={isOlderDup ? "bg-rose-50/40" : (isDup ? "bg-amber-50/40" : "")}>
                    <td className="py-2 pr-2">
                      <Checkbox
                        checked={selectedHistoryIds.has(rec.id)}
                        onCheckedChange={() => toggleHistoryOne(rec.id)}
                        data-testid={`btr-history-select-${rec.id}`}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">{rec.name_used}</div>
                      <div className="text-[11px] text-gray-500">Exnum {rec.exnum} · from {rec.source_id_doc || "ID"}</div>
                    </td>
                    <td className="py-2 pr-3">{rec.dob_used}</td>
                    <td className="py-2 pr-3">{rec.intake_date}</td>
                    <td className="py-2 pr-3">{rec.test_date}</td>
                    <td className="py-2 pr-3 tabular-nums">{rec.reading_score} / {rec.writing_score} / {rec.writeplacer_score}</td>
                    <td className="py-2 pr-3 text-[11px] text-gray-500">{new Date(rec.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handlePreviewRecord(rec)} data-testid={`btr-preview-record-${rec.id}`}>
                        <FileText size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleDownloadRecord(rec)} data-testid={`btr-download-record-${rec.id}`}>
                        <DownloadSimple size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" disabled={deletingId === rec.id} onClick={() => handleDeleteRecord(rec)} data-testid={`btr-delete-record-${rec.id}`}>
                        {deletingId === rec.id ? <CircleNotch size={13} className="animate-spin" /> : <Trash size={13} />}
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
